import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import { useAuth } from "@/contexts/AuthContext";
import { CustomerAPI, InvoiceAPI, extractError } from "@/lib/api";

const inr = (m) => `₹${(Math.round((m || 0) / 100)).toLocaleString("en-IN")}`;

export default function InvoiceNewPage() {
    const nav = useNavigate();
    const { branches, currentBranchId } = useAuth();
    const [branchId, setBranchId] = useState(currentBranchId || "");
    const [customers, setCustomers] = useState([]);
    const [customerId, setCustomerId] = useState("");
    const [lines, setLines] = useState([
        { line_type: "misc", description: "", quantity: 1, unit_price_minor: 0, tax_rate_percent: 18, hsn_sac_code: "9987" },
    ]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        CustomerAPI.list({ page_size: 100 }).then((d) => setCustomers(d.items || []));
    }, []);

    const addLine = () => setLines([...lines, { line_type: "misc", description: "", quantity: 1, unit_price_minor: 0, tax_rate_percent: 18, hsn_sac_code: "9987" }]);
    const setLine = (i, patch) => setLines(lines.map((l, j) => (i === j ? { ...l, ...patch } : l)));
    const remove = (i) => setLines(lines.filter((_, j) => j !== i));

    const subtotal = useMemo(
        () => lines.reduce((acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.unit_price_minor) || 0), 0),
        [lines],
    );
    const tax = useMemo(
        () => lines.reduce((acc, l) => acc + Math.round((Number(l.quantity) || 0) * (Number(l.unit_price_minor) || 0) * (Number(l.tax_rate_percent) || 0) / 100), 0),
        [lines],
    );

    const submit = async () => {
        if (!branchId || !customerId || lines.length === 0) { toast.error("Branch, customer and at least one line are required"); return; }
        setSubmitting(true);
        try {
            const created = await InvoiceAPI.create({
                branch_id: branchId, customer_id: customerId,
                lines: lines.map((l) => ({
                    line_type: l.line_type, description: l.description,
                    quantity: Number(l.quantity), unit_price_minor: Number(l.unit_price_minor),
                    tax_rate_percent: Number(l.tax_rate_percent), hsn_sac_code: l.hsn_sac_code,
                })),
            });
            toast.success(`Created invoice ${created.invoice_number}`);
            nav(`/invoices/${created.id}`);
        } catch (e) { toast.error(extractError(e)); }
        finally { setSubmitting(false); }
    };

    return (
        <div className="mx-auto max-w-4xl space-y-6" data-testid="invoice-new-page">
            <Link to="/invoices" className="inline-flex items-center text-sm text-zinc-500">
                <ArrowLeft className="mr-1 h-4 w-4" />Back to invoices
            </Link>
            <header>
                <h1 className="font-display text-3xl font-extrabold tracking-tight">New Invoice</h1>
                <p className="mt-1 text-sm text-zinc-500">Draft now, issue when ready. Numbers are gapless per branch per FY.</p>
            </header>
            <Card>
                <CardContent className="space-y-5 p-5">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-1.5"><Label>Branch</Label>
                            <Select value={branchId} onValueChange={setBranchId}>
                                <SelectTrigger data-testid="invoice-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                                <SelectContent>{(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5"><Label>Customer</Label>
                            <Select value={customerId} onValueChange={setCustomerId}>
                                <SelectTrigger data-testid="invoice-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <div className="mb-2 flex items-center justify-between">
                            <Label>Lines</Label>
                            <Button size="sm" variant="outline" onClick={addLine} data-testid="invoice-add-line-btn"><Plus className="mr-1 h-3 w-3" />Add line</Button>
                        </div>
                        <div className="space-y-2">
                            {lines.map((ln, idx) => (
                                <div key={idx} className="grid grid-cols-12 items-end gap-2 rounded-md border border-zinc-200 p-2 dark:border-zinc-800"
                                     data-testid={`invoice-new-line-${idx}`}>
                                    <div className="col-span-2">
                                        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Type</Label>
                                        <Select value={ln.line_type} onValueChange={(v) => setLine(idx, { line_type: v })}>
                                            <SelectTrigger data-testid={`invoice-new-line-${idx}-type`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="misc">Misc</SelectItem>
                                                <SelectItem value="labour">Labour</SelectItem>
                                                <SelectItem value="part">Part</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="col-span-4">
                                        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Description</Label>
                                        <Input value={ln.description} onChange={(e) => setLine(idx, { description: e.target.value })} data-testid={`invoice-new-line-${idx}-desc`} />
                                    </div>
                                    <div className="col-span-1">
                                        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Qty</Label>
                                        <Input type="number" value={ln.quantity} onChange={(e) => setLine(idx, { quantity: Number(e.target.value) })} data-testid={`invoice-new-line-${idx}-qty`} />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Rate (minor)</Label>
                                        <Input type="number" value={ln.unit_price_minor} onChange={(e) => setLine(idx, { unit_price_minor: Number(e.target.value) })} data-testid={`invoice-new-line-${idx}-rate`} />
                                    </div>
                                    <div className="col-span-1">
                                        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Tax %</Label>
                                        <Input type="number" value={ln.tax_rate_percent} onChange={(e) => setLine(idx, { tax_rate_percent: Number(e.target.value) })} />
                                    </div>
                                    <div className="col-span-1">
                                        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">HSN</Label>
                                        <Input value={ln.hsn_sac_code} onChange={(e) => setLine(idx, { hsn_sac_code: e.target.value })} />
                                    </div>
                                    <div className="col-span-1 flex justify-end">
                                        <Button size="icon" variant="ghost" onClick={() => remove(idx)} data-testid={`invoice-new-line-${idx}-remove`}>
                                            <Trash2 className="h-4 w-4 text-brand-red" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-between border-t border-zinc-200 pt-3 text-sm font-mono dark:border-zinc-800">
                        <div className="text-zinc-500">{lines.length} line{lines.length !== 1 ? "s" : ""}</div>
                        <div className="space-x-3">
                            <span className="text-zinc-500">Subtotal {inr(subtotal)}</span>
                            <span className="text-zinc-500">Tax {inr(tax)}</span>
                            <span className="text-base font-bold" data-testid="invoice-new-total">Total {inr(subtotal + tax)}</span>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => nav("/invoices")}>Cancel</Button>
                        <Button onClick={submit} disabled={submitting} data-testid="invoice-new-submit">
                            {submitting ? "Creating…" : "Save draft"}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
